import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { API_BASE, PHONE_DISPLAY, PHONE_TEL } from "../config";

const COPY = {
  quote: {
    eyebrow: "Request a Quote",
    title: "Get a quote for your site",
    lead: "Tell us about your site. A Kinitas specialist will call you within 24 hours to spec your kiosk and send a quote.",
    button: "Request my quote",
  },
  leasing: {
    eyebrow: "Leasing",
    title: "See lease options",
    lead: "Tell us about your site and we'll send monthly lease options within 24 hours.",
    button: "Send me lease options",
  },
  contact: {
    eyebrow: "Talk to an Expert",
    title: "Let's talk about your operation",
    lead: "Leave your details and a Kinitas specialist will call you within 24 hours.",
    button: "Contact me",
  },
};

const INDUSTRIES = [
  { value: "car-wash", label: "Car wash" },
  { value: "parking", label: "Parking" },
  { value: "other", label: "Other" },
];

const EMPTY = { name: "", company: "", email: "", phone: "", industry: "", sites: "", message: "", website: "" };

const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Enter your name.";
  if (f.company.trim().length < 2) e.company = "Enter your company.";
  if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) e.email = "Enter a valid email.";
  const digits = f.phone.replace(/\D/g, "").length;
  if (digits < 10 || digits > 15) e.phone = "Enter a valid phone number.";
  if (!f.industry) e.industry = "Choose an industry.";
  return e;
};

const inputCls = (err) =>
  `w-full px-4 py-3 rounded-lg border text-sm text-[#111827] placeholder-gray-400 bg-white focus:outline-none transition-colors duration-200 ${
    err ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-[#1a1f6c]"
  }`;

const Field = ({ id, label, error, optional, children }) => (
  <div className="text-left">
    <label htmlFor={id} className="block text-xs font-semibold text-[#374151] mb-1.5">
      {label}
      {optional && <span className="font-normal text-gray-400"> (optional)</span>}
    </label>
    {children}
    {error && (
      <p id={`${id}-error`} className="text-red-500 text-xs mt-1">
        {error}
      </p>
    )}
  </div>
);

/**
 * Formulario de cotización. `prefill` abre el modal (null lo cierra) y trae
 * { intent: 'quote'|'leasing'|'contact', product?, industry?, email?, source? }.
 */
export const ContactModal = ({ prefill, onClose }) => {
  const isOpen = !!prefill;
  // Se conserva el último prefill para que el contenido no desaparezca durante la animación de salida.
  const [data, setData] = useState({ intent: "contact" });
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");
  const firstFieldRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!prefill) return;
    setData(prefill);
    setForm((prev) => ({
      ...EMPTY,
      // Si el visitante ya escribió algo y cerró sin enviar, no se lo borramos.
      ...(status === "sent" ? {} : prev),
      email: prefill.email || (status === "sent" ? "" : prev.email),
      industry: prefill.industry || (status === "sent" ? "" : prev.industry),
      website: "",
    }));
    setErrors({});
    setStatus("idle");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  useEffect(() => {
    if (!isOpen) return;
    const prevFocus = document.activeElement;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      // Mantiene el foco dentro del diálogo.
      if (e.key === "Tab" && dialogRef.current) {
        const items = dialogRef.current.querySelectorAll("a[href], button:not([disabled]), input:not([tabindex='-1']), select, textarea");
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => firstFieldRef.current?.focus(), 250);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "unset";
      if (prevFocus && prevFocus.focus) prevFocus.focus();
    };
  }, [isOpen, onClose]);

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: validate({ ...form, [k]: v })[k] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) {
      dialogRef.current?.querySelector(`#cf-${Object.keys(errs)[0]}`)?.focus();
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${API_BASE}/landing/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          intent: data.intent,
          product: data.product || "",
          source: `${window.location.pathname}${data.source ? ` · ${data.source}` : ""}`,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.ok === false) throw new Error(body.msg || "");
      setStatus("sent");
    } catch (err) {
      setErrorMsg(err.message || "We couldn't send your request.");
      setStatus("error");
    }
  };

  const copy = COPY[data.intent] || COPY.contact;
  const firstName = form.name.trim().split(" ")[0];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[100]"
            style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[101] flex items-center justify-center px-4 py-6 pointer-events-none">
            <motion.div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="contact-title"
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-full overflow-y-auto pointer-events-auto"
              initial={{ opacity: 0, y: 36, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 36, scale: 0.96 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
            >
              <button
                type="button"
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <AnimatePresence exitBeforeEnter initial={false}>
                {status === "sent" ? (
                  <motion.div
                    key="sent"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.22 }}
                    className="px-8 py-12 sm:px-10 text-center"
                  >
                    <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
                      <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h2 id="contact-title" className="text-2xl font-bold text-[#111827] mb-3">
                      Thanks{firstName ? `, ${firstName}` : ""}! We got your request.
                    </h2>
                    <p className="text-sm text-[#6b7280] leading-relaxed mb-6">
                      A Kinitas specialist will call you within 24 hours. Need it sooner?
                    </p>
                    <a
                      href={PHONE_TEL}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#1a1f6c] text-white font-medium text-sm hover:bg-[#2832c2] transition-colors duration-300"
                    >
                      Call {PHONE_DISPLAY}
                    </a>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    noValidate
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="px-6 py-8 sm:px-10 sm:py-10"
                  >
                    <div className="text-center mb-6">
                      <p className="text-xs font-semibold uppercase tracking-widest text-[#1a1f6c] mb-2">{copy.eyebrow}</p>
                      <h2 id="contact-title" className="text-2xl sm:text-3xl font-bold text-[#111827] leading-tight mb-3">
                        {copy.title}
                      </h2>
                      <p className="text-sm text-[#6b7280] leading-relaxed">{copy.lead}</p>
                      {data.product && (
                        <span className="inline-block mt-3 text-xs font-medium text-[#1a1f6c] bg-[#1a1f6c]/[0.07] rounded-full px-3 py-1">
                          {data.product}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field id="cf-name" label="Full name" error={errors.name}>
                        <input ref={firstFieldRef} id="cf-name" autoComplete="name" value={form.name} onChange={set("name")}
                          aria-invalid={!!errors.name} aria-describedby={errors.name ? "cf-name-error" : undefined}
                          className={inputCls(errors.name)} placeholder="Jane Smith" />
                      </Field>
                      <Field id="cf-company" label="Company" error={errors.company}>
                        <input id="cf-company" autoComplete="organization" value={form.company} onChange={set("company")}
                          aria-invalid={!!errors.company} aria-describedby={errors.company ? "cf-company-error" : undefined}
                          className={inputCls(errors.company)} placeholder="Sunshine Car Wash" />
                      </Field>
                      <Field id="cf-email" label="Work email" error={errors.email}>
                        <input id="cf-email" type="email" autoComplete="email" value={form.email} onChange={set("email")}
                          aria-invalid={!!errors.email} aria-describedby={errors.email ? "cf-email-error" : undefined}
                          className={inputCls(errors.email)} placeholder="jane@company.com" />
                      </Field>
                      <Field id="cf-phone" label="Phone" error={errors.phone}>
                        <input id="cf-phone" type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")}
                          aria-invalid={!!errors.phone} aria-describedby={errors.phone ? "cf-phone-error" : undefined}
                          className={inputCls(errors.phone)} placeholder="(555) 123-4567" />
                      </Field>
                      <Field id="cf-industry" label="Industry" error={errors.industry}>
                        <select id="cf-industry" value={form.industry} onChange={set("industry")}
                          aria-invalid={!!errors.industry} aria-describedby={errors.industry ? "cf-industry-error" : undefined}
                          className={`${inputCls(errors.industry)} ${form.industry ? "" : "text-gray-400"}`}>
                          <option value="" disabled>Select one</option>
                          {INDUSTRIES.map((i) => (
                            <option key={i.value} value={i.value} className="text-[#111827]">{i.label}</option>
                          ))}
                        </select>
                      </Field>
                      <Field id="cf-sites" label="Number of sites or lanes" optional>
                        <input id="cf-sites" value={form.sites} onChange={set("sites")} maxLength={40}
                          className={inputCls()} placeholder="e.g. 2 sites, 6 lanes" />
                      </Field>
                      <div className="sm:col-span-2">
                        <Field id="cf-message" label="Anything we should know?" optional>
                          <textarea id="cf-message" rows={3} value={form.message} onChange={set("message")} maxLength={2000}
                            className={`${inputCls()} resize-none`} placeholder="Timeline, payment types, questions…" />
                        </Field>
                      </div>
                    </div>

                    {/* Campo trampa para bots: invisible para personas y lectores de pantalla. */}
                    <input type="text" name="website" value={form.website} onChange={set("website")}
                      tabIndex={-1} autoComplete="off" aria-hidden="true"
                      style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />

                    <button
                      type="submit"
                      disabled={status === "sending"}
                      className="mt-6 w-full px-6 py-3.5 rounded-lg bg-[#1a1f6c] text-white font-medium text-sm hover:bg-[#2832c2] transition-colors duration-300 disabled:opacity-60"
                    >
                      {status === "sending" ? "Sending…" : copy.button}
                    </button>

                    <AnimatePresence>
                      {status === "error" && (
                        <motion.p
                          role="alert"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="text-red-500 text-xs mt-3 text-center overflow-hidden"
                        >
                          {errorMsg} You can also call us at{" "}
                          <a href={PHONE_TEL} className="font-semibold underline">{PHONE_DISPLAY}</a>.
                        </motion.p>
                      )}
                    </AnimatePresence>

                    <p className="text-xs text-gray-400 mt-4 text-center">
                      Prefer to talk now? Call{" "}
                      <a href={PHONE_TEL} className="font-semibold text-[#1a1f6c] hover:underline">{PHONE_DISPLAY}</a>
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
